import React, { useEffect, useMemo, useState } from 'react';

import { fetchStaffList } from '../../services/api/staffApi';
import { resolveActionPending } from '../../services/api/visitActionPendingApi';
import { openReferralReceiptPdf } from '../../services/api/patientPlanApi';
import { formatStaffName } from '../../utils/staffName';
import { visitPaymentDueRupees } from '../../utils/visitMoney';

/**
 * Phase 3.1 — ReferralResolveDialog.
 *
 * Receptionist forwards a parked REFERRAL_TO_DOCTOR overlay to the
 * destination doctor. Pre-fills `resolved_target_doctor_id` with the
 * suggested target from the action-pending row, but Receptionist may
 * override (e.g. Dr B is off shift → route to Dr C). If a payment is
 * due, includes a payment-collected input.
 *
 * Props:
 *   visit       — visit row carrying action_pending_target_doctor_id +
 *                 action_pending_payment_due (rupees, Phase 3.5).
 *   onSubmitted — (response) => void on successful resolve.
 *   onCancel    — () => void
 */
export default function ReferralResolveDialog({ visit, onSubmitted, onCancel }) {
  const suggestedTargetId = visit?.action_pending_target_doctor_id || '';
  // Visit-row field is rupees (Phase 3.5). UI works in rupees end-to-end;
  // the only place paise appears is the wire-name of the API field
  // (`payment_collected_paise`) — converted inline at submit time below.
  const paymentDueRupees = visitPaymentDueRupees(visit);
  const referralReason = visit?.action_pending_referral_reason;

  const [resolvedTargetId, setResolvedTargetId] = useState(suggestedTargetId);
  // Receptionist-facing field is rupees (decimal).
  const [paymentCollectedStr, setPaymentCollectedStr] = useState(
    paymentDueRupees > 0 ? paymentDueRupees.toFixed(2) : '',
  );
  const [comment, setComment] = useState('');

  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [resolved, setResolved] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setStaffLoading(true);
    setStaffError(null);
    fetchStaffList()
      .then((res) => {
        if (cancelled) return;
        if (res?.success) setStaff(res.data || []);
        else setStaffError(res?.error || 'Failed to load staff');
      })
      .finally(() => { if (!cancelled) setStaffLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const doctors = useMemo(
    () => (staff || [])
      .filter((m) => (m.role || '').toUpperCase() === 'DOCTOR')
      .filter((m) => (m.status || 'ACTIVE').toUpperCase() === 'ACTIVE'),
    [staff],
  );

  const overrideActive = (
    !!resolvedTargetId
    && !!suggestedTargetId
    && resolvedTargetId !== suggestedTargetId
  );

  // Receptionist-facing amount in rupees. Empty / invalid → 0.
  // Accepts "500", "500.00", "500.5", etc.
  const paymentCollectedRupees = (() => {
    const trimmed = (paymentCollectedStr || '').trim();
    if (!trimmed) return 0;
    const rupees = parseFloat(trimmed);
    if (!Number.isFinite(rupees) || rupees < 0) return 0;
    return rupees;
  })();

  const canSubmit = !!resolvedTargetId && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      const payload = {
        resolution: 'RESOLVED',
        comment: comment.trim() || null,
        resolved_target_doctor_id: resolvedTargetId,
      };
      // API wire-field is named `payment_collected_paise` (legacy);
      // convert at the boundary only. Receptionist works in rupees.
      if (paymentDueRupees > 0 || paymentCollectedRupees > 0) {
        payload.payment_collected_paise = Math.round(paymentCollectedRupees * 100);
      }
      const resp = await resolveActionPending(visit.visit_id, payload);
      // Phase 3.1 telemetry.
      // eslint-disable-next-line no-console
      console.info(
        'metric=referral_resolved value=1 visit=%s overridden=%s collected_rupees=%s',
        visit.visit_id, overrideActive ? 'true' : 'false',
        paymentCollectedRupees.toFixed(2),
      );
      if (overrideActive) {
        // eslint-disable-next-line no-console
        console.info(
          'metric=referral_target_overridden value=1 visit=%s suggested=%s resolved=%s',
          visit.visit_id, suggestedTargetId, resolvedTargetId,
        );
      }
      if (paymentCollectedRupees > 0) {
        setResolved(true);
      } else {
        onSubmitted?.(resp);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setErrorMsg(typeof detail === 'string' ? detail : 'Failed to forward');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg shadow-xl p-5"
      >
        <h2 className="text-lg font-semibold mb-1">
          Forward referral
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
          Patient: <strong>{visit?.patient_name || visit?.visit_id}</strong>
          {visit?.queue_number != null && (
            <span> · #{visit.queue_number}</span>
          )}
        </p>

        {referralReason && (
          <div className="mb-3 rounded-md border border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/30 px-3 py-2 text-xs text-orange-900 dark:text-orange-100">
            <span className="font-semibold">Reason: </span>
            <span className="italic">{referralReason}</span>
          </div>
        )}

        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">
            Forward to doctor *
          </label>
          {staffLoading ? (
            <div className="text-xs text-slate-500 dark:text-slate-400 py-2">
              Loading…
            </div>
          ) : staffError ? (
            <div className="text-xs text-rose-600 dark:text-rose-300 py-2">
              {staffError}
            </div>
          ) : (
            <select
              value={resolvedTargetId}
              onChange={(e) => setResolvedTargetId(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
              required
            >
              <option value="">— choose doctor —</option>
              {doctors.map((m) => {
                const name = formatStaffName(m) || m.member_id;
                const isSuggested = m.member_id === suggestedTargetId;
                return (
                  <option key={m.member_id} value={m.member_id}>
                    {name}{isSuggested ? ' · suggested' : ''}
                  </option>
                );
              })}
            </select>
          )}
          {overrideActive && (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
              Overriding the doctor's suggestion. Both will be recorded.
            </p>
          )}
        </div>

        {paymentDueRupees > 0 && (
          <div className="mb-3">
            <label className="block text-sm font-medium mb-1">
              Payment collected in ₹ (quoted ₹{paymentDueRupees.toFixed(2)})
            </label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={paymentCollectedStr}
              onChange={(e) => setPaymentCollectedStr(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            />
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              Enter the amount in rupees. Adjust if the actual receipt differs.
            </p>
          </div>
        )}

        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">
            Note (optional)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            placeholder="e.g. Dr B unavailable, routed to Dr C"
          />
        </div>

        {errorMsg && (
          <div className="mb-3 rounded-md border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30 px-3 py-2 text-xs text-rose-700 dark:text-rose-200">
            {errorMsg}
          </div>
        )}

        {resolved ? (
          <div className="rounded-md border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-3 mb-3">
            <p className="text-sm font-medium text-emerald-800 dark:text-emerald-100 mb-2">
              Referral forwarded &amp; payment recorded.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={async () => {
                  setReceiptLoading(true);
                  await openReferralReceiptPdf(visit.visit_id);
                  setReceiptLoading(false);
                }}
                disabled={receiptLoading}
                className="px-3 py-1.5 text-sm rounded-md border border-emerald-400 dark:border-emerald-600 bg-white dark:bg-slate-800 text-emerald-800 dark:text-emerald-100 hover:bg-emerald-50 dark:hover:bg-emerald-900/40 disabled:opacity-60"
              >
                {receiptLoading ? 'Loading…' : 'View Receipt'}
              </button>
              <button
                type="button"
                onClick={() => onSubmitted?.()}
                className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-1.5 text-sm rounded-md bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60"
            >
              {busy ? 'Forwarding…' : 'Forward to doctor'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
